import { Entity, FieldType, PersistenceType } from 'gossamer-unified';
import { UserView } from './UserView';
import { LocationTrackingSchedule } from './LocationTrackingSchedule';
import { Location } from './Location';
import { Institution } from './Institution';

export interface LocationCheckLogRow {
  id: number;
  user_id: string;
  location_tracking_schedule_id: number;
  location_id: number;
  institution_id: number;
  check_status: string;
  user_latitude: number | null;
  user_longitude: number | null;
  target_latitude: number | null;
  target_longitude: number | null;
  distance_meters: number | null;
  error_message: string | null;
  checked_at: string;
  created_at: string;
}

export const LocationCheckLog: Entity<LocationCheckLogRow> = {
  id: 'LocationCheckLog',
  title: 'Location Check Log',
  service: 'main',
  rowLabel: (row: LocationCheckLogRow) => `${row.check_status} - ${new Date(row.checked_at).toLocaleString()}`,
  primaryKeyFields: ['id'],
  source: {
    tableName: 'public.location_check_log',
  },
  security: [{ role: 'staff', action: 'all', access: 'all' }],
  defaultSort: [['checked_at', 'DESC']],
  defaultFilters: [],
  fields: {
    id: {
      seq: 5,
      type: FieldType.Integer,
      label: 'ID',
      persistence: PersistenceType.BackEndManaged,
      mandatory: true,
      listColumn: true,
      autoIncrement: true,
    },
    user_id: {
      seq: 10,
      type: FieldType.Reference,
      label: 'User',
      refEntity: UserView,
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
    },
    location_tracking_schedule_id: {
      seq: 15,
      type: FieldType.Reference,
      label: 'Tracking Schedule',
      refEntity: LocationTrackingSchedule,
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
    },
    location_id: {
      seq: 20,
      type: FieldType.Reference,
      label: 'Location',
      refEntity: Location,
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
    },
    institution_id: {
      seq: 25,
      type: FieldType.Reference,
      label: 'Institution',
      refEntity: Institution,
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
    },
    check_status: {
      seq: 30,
      type: FieldType.Options,
      label: 'Check Status',
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
      options: [
        { id: 'success', label: 'Success' },
        { id: 'out_of_range', label: 'Out of Range' },
        { id: 'permission_denied', label: 'Permission Denied' },
        { id: 'location_unavailable', label: 'Location Unavailable' },
        { id: 'error', label: 'Error' },
      ],
      description: 'Result of the location check',
    },
    user_latitude: {
      seq: 35,
      type: FieldType.Decimal,
      label: 'User Latitude',
      listColumn: false,
      persistence: PersistenceType.BackEndManaged,
      description: "User's latitude at time of check",
    },
    user_longitude: {
      seq: 40,
      type: FieldType.Decimal,
      label: 'User Longitude',
      listColumn: false,
      persistence: PersistenceType.BackEndManaged,
      description: "User's longitude at time of check",
    },
    target_latitude: {
      seq: 45,
      type: FieldType.Decimal,
      label: 'Target Latitude',
      listColumn: false,
      persistence: PersistenceType.BackEndManaged,
      description: 'Target location latitude',
    },
    target_longitude: {
      seq: 50,
      type: FieldType.Decimal,
      label: 'Target Longitude',
      listColumn: false,
      persistence: PersistenceType.BackEndManaged,
      description: 'Target location longitude',
    },
    distance_meters: {
      seq: 55,
      type: FieldType.Decimal,
      label: 'Distance (meters)',
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
      description: 'Calculated distance from target location',
    },
    error_message: {
      seq: 60,
      type: FieldType.Text,
      label: 'Error Message',
      listColumn: false,
      persistence: PersistenceType.BackEndManaged,
      description: 'Error message if check failed',
      maxLength: 500,
    },
    checked_at: {
      seq: 65,
      type: FieldType.Datetime,
      label: 'Checked At',
      mandatory: true,
      listColumn: true,
      persistence: PersistenceType.BackEndManaged,
      description: 'When the location check was performed',
    },
    created_at: {
      seq: 70,
      type: FieldType.Datetime,
      label: 'Created At',
      persistence: PersistenceType.BackEndManaged,
    },
  },
};
